import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import {
  Button,
  ColorPalette,
  ConfirmDialog,
  EmptyState,
  ErrorNote,
  Field,
  Loading,
  Modal,
  Tabs,
  Toggle,
  compactInputClass,
  inputClass,
} from '../../components/ui';
import { errorMessage } from '../../lib/api';
import { formatShortDate } from '../../lib/format';
import {
  connectionsApi,
  departmentsApi,
  instagramApi,
  whatsappApi,
  webhookUrl,
  workingHoursApi,
  type ConnectionPayload,
} from '../../lib/resources';
import type { ChannelConnection, DaySchedule, Department, WorkingHours } from '../../types';

const DAY_NAMES = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];

const DEFAULT_SCHEDULE: DaySchedule[] = [0, 1, 2, 3, 4, 5, 6].map((day) => ({
  day,
  enabled: day >= 1 && day <= 5,
  intervals: [{ start: '09:00', end: '17:00' }],
}));

function SectionHeader({
  title,
  subtitle,
  action,
}: {
  title: string;
  subtitle: string;
  action?: React.ReactNode;
}) {
  return (
    <header className="mb-4 flex items-start justify-between gap-3">
      <div>
        <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
        <p className="text-sm text-slate-500">{subtitle}</p>
      </div>
      {action}
    </header>
  );
}

const minutesOf = (hhmm: string) => {
  const [hours, minutes] = hhmm.split(':').map(Number);
  return (hours || 0) * 60 + (minutes || 0);
};

/** "Seg–Sex", "Seg, Qua" … */
function describeDays(schedule: DaySchedule[]): string {
  const on = schedule.filter((day) => day.enabled).map((day) => DAY_NAMES[day.day].slice(0, 3));
  return on.length ? on.join(', ') : 'Nenhum dia';
}

function dailyAverage(schedule: DaySchedule[]): string {
  const days = schedule.filter((day) => day.enabled);
  if (!days.length) return '—';
  const total = days.reduce(
    (sum, day) =>
      sum +
      day.intervals.reduce(
        (inner, interval) =>
          inner + Math.max(0, minutesOf(interval.end) - minutesOf(interval.start)),
        0,
      ),
    0,
  );
  const average = total / days.length;
  return `${Math.floor(average / 60)}h${average % 60 ? ` ${Math.round(average % 60)}min` : ''}`;
}

// ------------------------------------------------------- Horários de trabalho

export function WorkingHoursSection() {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<WorkingHours | 'new' | null>(null);
  const [removing, setRemoving] = useState<WorkingHours | null>(null);
  const query = useQuery({
    queryKey: ['working-hours'],
    queryFn: () => workingHoursApi.list(),
  });
  const remove = useMutation({
    mutationFn: (id: string) => workingHoursApi.remove(id),
    onSuccess: () => {
      setRemoving(null);
      queryClient.invalidateQueries({ queryKey: ['working-hours'] });
    },
  });
  const rows = query.data?.data ?? [];

  return (
    <section>
      <SectionHeader
        title="Horários de trabalho"
        subtitle="Expediente dos departamentos. O tempo de espera dos atendimentos conta só dentro dele."
        action={
          <Button variant="primary" onClick={() => setEditing('new')}>
            Criar horário
          </Button>
        }
      />
      {query.isLoading ? (
        <Loading />
      ) : rows.length === 0 ? (
        <EmptyState
          title="Nenhum horário de trabalho"
          hint="Crie um e ligue aos departamentos."
        />
      ) : (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="px-4 py-2">Nome</th>
                <th className="px-4 py-2">Dias</th>
                <th className="px-4 py-2">Fuso</th>
                <th className="px-4 py-2">Média diária</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((row) => (
                <tr key={row.id}>
                  <td className="px-4 py-2 font-medium text-slate-800">{row.name}</td>
                  <td className="px-4 py-2 text-slate-600">{describeDays(row.schedule)}</td>
                  <td className="px-4 py-2 text-slate-500">{row.timezone}</td>
                  <td className="px-4 py-2 text-slate-600">{dailyAverage(row.schedule)}</td>
                  <td className="px-4 py-2 text-right">
                    <button
                      type="button"
                      onClick={() => setEditing(row)}
                      className="mr-3 text-xs text-slate-600 hover:underline"
                    >
                      Editar
                    </button>
                    <button
                      type="button"
                      onClick={() => setRemoving(row)}
                      className="text-xs text-red-600 hover:underline"
                    >
                      Excluir
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {editing && (
        <WorkingHoursForm
          item={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => queryClient.invalidateQueries({ queryKey: ['working-hours'] })}
        />
      )}
      {removing && (
        <ConfirmDialog
          title="Excluir horário"
          message={`"${removing.name}" vai para a Lixeira. Departamentos que o usam ficam sem horário.`}
          confirmLabel="Excluir"
          danger
          busy={remove.isPending}
          error={remove.error ? errorMessage(remove.error) : null}
          onConfirm={() => remove.mutate(removing.id)}
          onClose={() => setRemoving(null)}
        />
      )}
    </section>
  );
}

function WorkingHoursForm({
  item,
  onClose,
  onSaved,
}: {
  item: WorkingHours | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(item?.name ?? '');
  const [timezone, setTimezone] = useState(item?.timezone ?? 'America/Sao_Paulo');
  const [schedule, setSchedule] = useState<DaySchedule[]>(item?.schedule ?? DEFAULT_SCHEDULE);
  const save = useMutation({
    mutationFn: () => {
      const payload = { name: name.trim(), timezone, schedule } as Partial<WorkingHours>;
      return item ? workingHoursApi.update(item.id, payload) : workingHoursApi.create(payload);
    },
    onSuccess: () => {
      onSaved();
      onClose();
    },
  });
  const setDay = (day: number, patch: Partial<DaySchedule>) =>
    setSchedule((current) =>
      current.map((entry) => (entry.day === day ? { ...entry, ...patch } : entry)),
    );

  return (
    <Modal
      title={item ? 'Editar horário de trabalho' : 'Criar horário de trabalho'}
      onClose={onClose}
      wide
    >
      <div className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Nome *">
            <input
              className={inputClass}
              value={name}
              maxLength={120}
              onChange={(event) => setName(event.target.value)}
            />
          </Field>
          <Field label="Fuso horário">
            <input
              className={inputClass}
              value={timezone}
              onChange={(event) => setTimezone(event.target.value)}
            />
          </Field>
        </div>
        <div className="divide-y divide-slate-100 rounded-lg border border-slate-200">
          {schedule.map((entry) => (
            <div key={entry.day} className="flex flex-wrap items-start gap-3 px-3 py-2">
              <div className="w-32 pt-1.5">
                <Toggle
                  checked={entry.enabled}
                  onChange={(enabled) => setDay(entry.day, { enabled })}
                  label={DAY_NAMES[entry.day]}
                />
              </div>
              {entry.enabled ? (
                <div className="flex flex-1 flex-col gap-1.5">
                  {entry.intervals.map((interval, index) => (
                    <div key={index} className="flex items-center gap-2 text-sm">
                      <input
                        type="time"
                        aria-label={`${DAY_NAMES[entry.day]} início ${index + 1}`}
                        className={`${compactInputClass} w-28`}
                        value={interval.start}
                        onChange={(event) =>
                          setDay(entry.day, {
                            intervals: entry.intervals.map((item, position) =>
                              position === index
                                ? { ...item, start: event.target.value }
                                : item,
                            ),
                          })
                        }
                      />
                      <span className="text-slate-400">–</span>
                      <input
                        type="time"
                        aria-label={`${DAY_NAMES[entry.day]} fim ${index + 1}`}
                        className={`${compactInputClass} w-28`}
                        value={interval.end}
                        onChange={(event) =>
                          setDay(entry.day, {
                            intervals: entry.intervals.map((item, position) =>
                              position === index ? { ...item, end: event.target.value } : item,
                            ),
                          })
                        }
                      />
                      {entry.intervals.length > 1 && (
                        <button
                          type="button"
                          aria-label="Remover intervalo"
                          onClick={() =>
                            setDay(entry.day, {
                              intervals: entry.intervals.filter(
                                (_, position) => position !== index,
                              ),
                            })
                          }
                          className="text-slate-400 hover:text-red-600"
                        >
                          ×
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() =>
                      setDay(entry.day, {
                        intervals: [...entry.intervals, { start: '13:00', end: '18:00' }],
                      })
                    }
                    className="self-start text-xs text-slate-600 hover:underline"
                  >
                    + intervalo
                  </button>
                </div>
              ) : (
                <span className="pt-2 text-xs text-slate-400">Fechado</span>
              )}
            </div>
          ))}
        </div>
        {save.error && (
          <ErrorNote>{errorMessage(save.error, 'Não foi possível salvar')}</ErrorNote>
        )}
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>Cancelar</Button>
          <Button
            variant="primary"
            disabled={!name.trim() || save.isPending}
            onClick={() => save.mutate()}
          >
            Salvar
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// ----------------------------------------------------------- Departamentos

export function DepartmentsSection() {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<Department | 'new' | null>(null);
  const [removing, setRemoving] = useState<Department | null>(null);
  const query = useQuery({ queryKey: ['departments'], queryFn: () => departmentsApi.list() });
  const hours = useQuery({
    queryKey: ['working-hours'],
    queryFn: () => workingHoursApi.list(),
  });
  const remove = useMutation({
    mutationFn: (id: string) => departmentsApi.remove(id),
    onSuccess: () => {
      setRemoving(null);
      queryClient.invalidateQueries({ queryKey: ['departments'] });
    },
  });
  const rows = query.data?.data ?? [];

  return (
    <section>
      <SectionHeader
        title="Departamentos"
        subtitle="Filas do chat. O principal recebe as conversas novas quando a conexão não define outro."
        action={
          <Button variant="primary" onClick={() => setEditing('new')}>
            Criar departamento
          </Button>
        }
      />
      {query.isLoading ? (
        <Loading />
      ) : rows.length === 0 ? (
        <EmptyState
          title="Nenhum departamento"
          hint="Crie o primeiro e marque-o como principal."
        />
      ) : (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="px-4 py-2">Departamento</th>
                <th className="px-4 py-2">Horário</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2">Criado</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((row) => (
                <tr key={row.id}>
                  <td className="px-4 py-2">
                    <span className="flex items-center gap-2 font-medium text-slate-800">
                      <span
                        className="h-2.5 w-2.5 rounded-full"
                        style={{ backgroundColor: row.color ?? '#cbd5e1' }}
                      />
                      {row.name}
                      {row.isMain && (
                        <span className="rounded bg-slate-900 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-white">
                          principal
                        </span>
                      )}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-slate-600">
                    {hours.data?.data.find((item) => item.id === row.workingHoursId)?.name ??
                      '—'}
                  </td>
                  <td className="px-4 py-2">
                    <span className={row.isActive ? 'text-emerald-700' : 'text-slate-400'}>
                      {row.isActive ? 'Ativo' : 'Inativo'}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-slate-500">{formatShortDate(row.createdAt)}</td>
                  <td className="px-4 py-2 text-right">
                    <button
                      type="button"
                      onClick={() => setEditing(row)}
                      className="mr-3 text-xs text-slate-600 hover:underline"
                    >
                      Editar
                    </button>
                    <button
                      type="button"
                      onClick={() => setRemoving(row)}
                      className="text-xs text-red-600 hover:underline"
                    >
                      Excluir
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {editing && (
        <DepartmentForm
          item={editing === 'new' ? null : editing}
          hours={hours.data?.data ?? []}
          onClose={() => setEditing(null)}
          onSaved={() => queryClient.invalidateQueries({ queryKey: ['departments'] })}
        />
      )}
      {removing && (
        <ConfirmDialog
          title="Excluir departamento"
          message={`"${removing.name}" vai para a Lixeira. As conversas antigas continuam com o histórico.`}
          confirmLabel="Excluir"
          danger
          busy={remove.isPending}
          error={remove.error ? errorMessage(remove.error) : null}
          onConfirm={() => remove.mutate(removing.id)}
          onClose={() => setRemoving(null)}
        />
      )}
    </section>
  );
}

function DepartmentForm({
  item,
  hours,
  onClose,
  onSaved,
}: {
  item: Department | null;
  hours: WorkingHours[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [tab, setTab] = useState<'config' | 'atendentes'>('config');
  const [name, setName] = useState(item?.name ?? '');
  const [color, setColor] = useState<string | null>(item?.color ?? '#F87171');
  const [isActive, setIsActive] = useState(item?.isActive ?? true);
  const [isMain, setIsMain] = useState(item?.isMain ?? false);
  const [workingHoursId, setWorkingHoursId] = useState(item?.workingHoursId ?? '');
  const save = useMutation({
    mutationFn: () => {
      const payload = {
        name: name.trim(),
        color: color ?? undefined,
        isActive,
        isMain,
        workingHoursId: workingHoursId || null,
      } as Partial<Department>;
      return item ? departmentsApi.update(item.id, payload) : departmentsApi.create(payload);
    },
    onSuccess: () => {
      onSaved();
      onClose();
    },
  });

  return (
    <Modal
      title={item ? `Departamento — ${item.name}` : 'Criar departamento'}
      onClose={onClose}
    >
      {item && (
        <div className="mb-4">
          <Tabs
            tabs={[
              ['config', 'Configuração'],
              ['atendentes', 'Atendentes'],
            ]}
            value={tab}
            onChange={setTab}
          />
        </div>
      )}
      {tab === 'atendentes' && item ? (
        <DepartmentMembers departmentId={item.id} onClose={onClose} />
      ) : (
        <div className="space-y-4">
          <Field label="Nome *">
            <input
              className={inputClass}
              value={name}
              maxLength={120}
              onChange={(event) => setName(event.target.value)}
            />
          </Field>
          <Field label="Horário de funcionamento">
            <select
              className={inputClass}
              value={workingHoursId}
              onChange={(event) => setWorkingHoursId(event.target.value)}
            >
              <option value="">Sem horário (tempo corrido)</option>
              {hours.map((entry) => (
                <option key={entry.id} value={entry.id}>
                  {entry.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Cor">
            <ColorPalette value={color} onChange={setColor} />
          </Field>
          <Toggle
            checked={isActive}
            onChange={setIsActive}
            label="Ativo"
            hint="Departamento inativo não recebe conversas novas."
          />
          <Toggle
            checked={isMain}
            onChange={setIsMain}
            label="Principal"
            hint="Recebe as conversas novas quando a conexão não define outro departamento."
          />
          {save.error && (
            <ErrorNote>{errorMessage(save.error, 'Não foi possível salvar')}</ErrorNote>
          )}
          <div className="flex justify-end gap-2">
            <Button onClick={onClose}>Cancelar</Button>
            <Button
              variant="primary"
              disabled={!name.trim() || save.isPending}
              onClick={() => save.mutate()}
            >
              {item ? 'Salvar' : 'Criar'}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}

function DepartmentMembers({
  departmentId,
  onClose,
}: {
  departmentId: string;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['department-members', departmentId],
    queryFn: () => departmentsApi.members(departmentId),
  });
  const [draft, setDraft] = useState<string[] | null>(null);
  const chosen =
    draft ?? (query.data ?? []).filter((row) => row.isMember).map((row) => row.membershipId);
  const save = useMutation({
    mutationFn: () => departmentsApi.setMembers(departmentId, chosen),
    onSuccess: (rows) => {
      queryClient.setQueryData(['department-members', departmentId], rows);
      setDraft(null);
    },
  });
  if (query.isLoading) return <Loading />;

  return (
    <div className="space-y-3">
      <p className="text-xs text-slate-500">
        Escolha quem atende neste departamento. É preciso ter um papel de multiatendimento.
      </p>
      <ul className="max-h-80 divide-y divide-slate-100 overflow-y-auto rounded-lg border border-slate-200">
        {(query.data ?? []).map((row) => (
          <li
            key={row.membershipId}
            className="flex items-center justify-between px-3 py-2 text-sm"
          >
            <label className={`flex items-center gap-2 ${row.canAttend ? '' : 'opacity-50'}`}>
              <input
                type="checkbox"
                disabled={!row.canAttend}
                checked={chosen.includes(row.membershipId)}
                onChange={(event) =>
                  setDraft(
                    event.target.checked
                      ? [...chosen, row.membershipId]
                      : chosen.filter((id) => id !== row.membershipId),
                  )
                }
              />
              <span className="font-medium text-slate-800">{row.name || row.email}</span>
              <span className="text-xs text-slate-400">{row.email}</span>
            </label>
            {!row.canAttend && (
              <span className="text-[11px] text-slate-400">sem papel de chat</span>
            )}
          </li>
        ))}
      </ul>
      {save.error && <ErrorNote>{errorMessage(save.error)}</ErrorNote>}
      <div className="flex items-center justify-end gap-2">
        {draft && (
          <span className="text-xs font-medium text-amber-600">Alterações não salvas</span>
        )}
        <Button onClick={onClose}>Fechar</Button>
        <Button
          variant="primary"
          disabled={!draft || save.isPending}
          onClick={() => save.mutate()}
        >
          Salvar atendentes
        </Button>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------- Conexões

const STATUS_STYLE: Record<ChannelConnection['status'], string> = {
  CONNECTED: 'bg-emerald-100 text-emerald-700',
  DISCONNECTED: 'bg-slate-100 text-slate-600',
  ERROR: 'bg-red-100 text-red-700',
};
const STATUS_LABEL: Record<ChannelConnection['status'], string> = {
  CONNECTED: 'Conectada',
  DISCONNECTED: 'Não conferida',
  ERROR: 'Erro',
};

/**
 * "Conectar Instagram" (IG1) e "Conectar WhatsApp" (WA1): abre a janela já no clique (para o
 * navegador não bloquear) e depois aponta para a URL assinada. A janela avisa por
 * postMessage; se a Meta cortar o `opener`, o fechamento basta.
 */
function useMetaConnect(
  kind: 'instagram' | 'whatsapp',
  connectUrl: () => Promise<{ url: string }>,
  onDone: () => void,
) {
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<{ ok: boolean; message: string } | null>(null);

  const start = async () => {
    setNotice(null);
    const popup = window.open('', `${kind}-connect`, 'width=560,height=760');
    setBusy(true);
    let finished = false;
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      const data = event.data as { type?: string; ok?: boolean; message?: string } | null;
      if (data?.type !== `${kind}-connect`) return;
      finish({ ok: Boolean(data.ok), message: data.message ?? '' });
    };
    const timer = window.setInterval(() => {
      if (!popup || popup.closed) finish();
    }, 800);
    function finish(result?: { ok: boolean; message: string }) {
      if (finished) return;
      finished = true;
      window.removeEventListener('message', onMessage);
      window.clearInterval(timer);
      setBusy(false);
      if (result) setNotice(result);
      onDone();
    }
    window.addEventListener('message', onMessage);
    try {
      const { url } = await connectUrl();
      if (popup) popup.location.href = url;
      else window.location.href = url;
    } catch (error) {
      popup?.close();
      finish({ ok: false, message: errorMessage(error) });
    }
  };

  return { start, busy, notice };
}

export function ConnectionsSection() {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<ChannelConnection | 'new' | null>(null);
  const [removing, setRemoving] = useState<ChannelConnection | null>(null);
  const [simulating, setSimulating] = useState<ChannelConnection | null>(null);
  const query = useQuery({ queryKey: ['connections'], queryFn: connectionsApi.list });
  const departments = useQuery({
    queryKey: ['departments'],
    queryFn: () => departmentsApi.list(),
  });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['connections'] });
  const instagram = useMetaConnect('instagram', instagramApi.connectUrl, refresh);
  const whatsapp = useMetaConnect('whatsapp', whatsappApi.connectUrl, refresh);
  const check = useMutation({ mutationFn: connectionsApi.check, onSuccess: refresh });
  const remove = useMutation({
    mutationFn: (id: string) => connectionsApi.remove(id),
    onSuccess: () => {
      setRemoving(null);
      refresh();
    },
  });
  const rows = query.data?.data ?? [];

  return (
    <section>
      <SectionHeader
        title="Conexões"
        subtitle="WhatsApp (Cloud API oficial, em coexistência com o app) e Instagram espelhados no chat."
        action={
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => void instagram.start()} disabled={instagram.busy}>
              {instagram.busy ? 'Aguardando a Meta…' : 'Conectar Instagram'}
            </Button>
            <Button
              variant="primary"
              onClick={() => void whatsapp.start()}
              disabled={whatsapp.busy}
            >
              {whatsapp.busy ? 'Aguardando a Meta…' : 'Conectar WhatsApp'}
            </Button>
            <Button onClick={() => setEditing('new')}>Conexão manual (Cloud API)</Button>
          </div>
        }
      />
      {[instagram.notice, whatsapp.notice].map(
        (notice, index) =>
          notice &&
          (notice.ok ? (
            <p
              key={index}
              className="mb-3 rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-700"
            >
              {notice.message}
            </p>
          ) : (
            <ErrorNote key={index}>{notice.message}</ErrorNote>
          )),
      )}
      {query.isLoading ? (
        <Loading />
      ) : rows.length === 0 ? (
        <EmptyState
          title="Nenhuma conexão"
          hint="Use Conectar WhatsApp ou Conectar Instagram e siga a janela da Meta."
        />
      ) : (
        <div className="space-y-3">
          {rows.map((row) => {
            const isInstagram = row.provider === 'INSTAGRAM_API';
            const ig = isInstagram ? row.config.instagram : undefined;
            const wa = row.config.whatsapp;
            return (
              <div key={row.id} className="rounded-xl border border-slate-200 bg-white p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="flex items-start gap-3">
                    {isInstagram &&
                      (ig?.profilePictureUrl ? (
                        <img
                          src={ig.profilePictureUrl}
                          alt=""
                          referrerPolicy="no-referrer"
                          className="h-10 w-10 rounded-full object-cover"
                        />
                      ) : (
                        <span className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-amber-400 via-pink-500 to-purple-600 text-sm font-semibold text-white">
                          IG
                        </span>
                      ))}
                    <div>
                      <p className="flex items-center gap-2 font-medium text-slate-900">
                        {row.name}
                        <span
                          className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${STATUS_STYLE[row.status]}`}
                        >
                          {STATUS_LABEL[row.status]}
                        </span>
                        {!row.isActive && (
                          <span className="text-xs text-slate-400">desativada</span>
                        )}
                      </p>
                      <p className="text-xs text-slate-500">
                        {row.provider === 'WHATSAPP_CLOUD_API'
                          ? wa
                            ? 'WhatsApp · coexistência com o app'
                            : 'WhatsApp Cloud API'
                          : isInstagram
                            ? `Instagram${ig ? ` · @${ig.username}` : ''}`
                            : row.provider}
                        {row.phoneNumber ? ` · ${row.phoneNumber}` : ''}
                        {' · '}
                        {departments.data?.data.find(
                          (item) => item.id === row.defaultDepartmentId,
                        )?.name ?? 'departamento principal'}
                      </p>
                      {row.errorMessage && (
                        <p className="mt-1 text-xs text-red-600">{row.errorMessage}</p>
                      )}
                      <p className="mt-1 text-[11px] text-slate-400">
                        Último evento:{' '}
                        {row.lastEventAt
                          ? new Date(row.lastEventAt).toLocaleString('pt-BR')
                          : 'nenhum'}
                        {ig &&
                          (ig.tokenExpiresAt
                            ? ` · acesso até ${new Date(ig.tokenExpiresAt).toLocaleDateString('pt-BR')}`
                            : ' · acesso sem vencimento')}
                      </p>
                      {wa?.subscribeError && (
                        <p className="text-xs text-amber-600">
                          Não recebe mensagens ainda — a conta do WhatsApp não foi ligada ao
                          webhook. Reconecte.
                        </p>
                      )}
                      {wa?.historyError && (
                        <p className="text-xs text-slate-500">
                          O histórico do celular não veio; as conversas novas espelham
                          normalmente.
                        </p>
                      )}
                      {ig?.subscribeError && (
                        <p className="text-xs text-amber-600">
                          Não recebe DMs nem comentários ainda — reconecte depois de dar a
                          permissão de Páginas (pages_manage_metadata).
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button onClick={() => check.mutate(row.id)} disabled={check.isPending}>
                      Conferir
                    </Button>
                    {isInstagram ? (
                      <Button onClick={() => void instagram.start()} disabled={instagram.busy}>
                        Reconectar
                      </Button>
                    ) : wa ? (
                      <Button onClick={() => void whatsapp.start()} disabled={whatsapp.busy}>
                        Reconectar
                      </Button>
                    ) : (
                      <Button onClick={() => setSimulating(row)}>Simular mensagem</Button>
                    )}
                    <Button onClick={() => setEditing(row)}>Editar</Button>
                    <Button variant="danger" onClick={() => setRemoving(row)}>
                      Excluir
                    </Button>
                  </div>
                </div>
                {!isInstagram && !wa && row.verifyToken !== undefined && (
                  <div className="mt-3 grid gap-2 rounded-lg bg-slate-50 p-3 text-xs sm:grid-cols-2">
                    <CopyField
                      label="URL do webhook (Callback URL)"
                      value={webhookUrl(row.webhook)}
                    />
                    <CopyField
                      label="Token de verificação (Verify token)"
                      value={row.verifyToken ?? ''}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      {check.error && <ErrorNote>{errorMessage(check.error)}</ErrorNote>}
      {simulating && (
        <SimulateModal
          connection={simulating}
          onClose={() => setSimulating(null)}
          onDone={() => {
            refresh();
            queryClient.invalidateQueries({ queryKey: ['conversations'] });
          }}
        />
      )}
      {editing && (
        <ConnectionForm
          item={editing === 'new' ? null : editing}
          departments={departments.data?.data ?? []}
          onClose={() => setEditing(null)}
          onSaved={refresh}
        />
      )}
      {removing && (
        <ConfirmDialog
          title="Excluir conexão"
          message={`"${removing.name}" vai para a Lixeira e para de receber mensagens. As conversas já espelhadas continuam.`}
          confirmLabel="Excluir"
          danger
          busy={remove.isPending}
          error={remove.error ? errorMessage(remove.error) : null}
          onConfirm={() => remove.mutate(removing.id)}
          onClose={() => setRemoving(null)}
        />
      )}
    </section>
  );
}

/** Simulador (Q2): manda uma mensagem de teste como se viesse da Meta. */
function SimulateModal({
  connection,
  onClose,
  onDone,
}: {
  connection: ChannelConnection;
  onClose: () => void;
  onDone: () => void;
}) {
  const [kind, setKind] = useState<'text' | 'echo'>('text');
  const [contactPhone, setContactPhone] = useState('5585988880000');
  const [name, setName] = useState('Contato de teste');
  const [body, setBody] = useState('Olá! Quero saber mais.');
  const run = useMutation({
    mutationFn: () =>
      connectionsApi.simulate(connection.id, { kind, contactPhone, name, body }),
    onSuccess: onDone,
  });

  return (
    <Modal title={`Simular mensagem — ${connection.name}`} onClose={onClose}>
      <div className="space-y-4">
        <p className="text-xs text-slate-500">
          Gera um evento no mesmo formato da Meta, sem número real. A mensagem aparece no Chat
          ao vivo.
        </p>
        <Field label="Quem manda">
          <select
            className={inputClass}
            value={kind}
            onChange={(event) => setKind(event.target.value as 'text' | 'echo')}
          >
            <option value="text">O contato (mensagem recebida)</option>
            <option value="echo">A empresa, pelo app do celular (eco)</option>
          </select>
        </Field>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Telefone do contato">
            <input
              className={inputClass}
              value={contactPhone}
              onChange={(event) => setContactPhone(event.target.value)}
            />
          </Field>
          <Field label="Nome do contato">
            <input
              className={inputClass}
              value={name}
              disabled={kind === 'echo'}
              onChange={(event) => setName(event.target.value)}
            />
          </Field>
        </div>
        <Field label="Mensagem">
          <textarea
            rows={3}
            className={inputClass}
            value={body}
            onChange={(event) => setBody(event.target.value)}
          />
        </Field>
        {run.data && (
          <p className="text-xs text-emerald-700">
            {run.data.messages ? 'Mensagem gravada.' : 'Nada gravado (mensagem repetida?).'}
          </p>
        )}
        {run.error && <ErrorNote>{errorMessage(run.error, 'A simulação falhou')}</ErrorNote>}
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>Fechar</Button>
          <Button
            variant="primary"
            disabled={!body.trim() || run.isPending}
            onClick={() => run.mutate()}
          >
            Simular
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function CopyField({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div>
      <p className="mb-1 font-medium text-slate-600">{label}</p>
      <div className="flex items-center gap-2">
        <code
          className="min-w-0 flex-1 truncate rounded bg-white px-2 py-1 text-slate-700"
          title={value}
        >
          {value || '—'}
        </code>
        <button
          type="button"
          onClick={() => {
            void navigator.clipboard?.writeText(value);
            setCopied(true);
            window.setTimeout(() => setCopied(false), 1500);
          }}
          className="shrink-0 text-slate-500 hover:text-slate-800"
        >
          {copied ? 'copiado' : 'copiar'}
        </button>
      </div>
    </div>
  );
}

function ConnectionForm({
  item,
  departments,
  onClose,
  onSaved,
}: {
  item: ChannelConnection | null;
  departments: Department[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(item?.name ?? '');
  const [phoneNumber, setPhoneNumber] = useState(item?.phoneNumber ?? '');
  const [externalId, setExternalId] = useState(item?.externalId ?? '');
  const [departmentId, setDepartmentId] = useState(item?.defaultDepartmentId ?? '');
  const [isActive, setIsActive] = useState(item?.isActive ?? true);
  const [accessToken, setAccessToken] = useState('');
  const [appSecret, setAppSecret] = useState('');
  const save = useMutation({
    mutationFn: () => {
      const secrets: ConnectionPayload['secrets'] = {};
      if (accessToken) secrets.accessToken = accessToken;
      if (appSecret) secrets.appSecret = appSecret;
      const payload: ConnectionPayload = {
        name: name.trim(),
        phoneNumber: phoneNumber.trim(),
        externalId: externalId.trim(),
        defaultDepartmentId: departmentId || null,
        isActive,
        ...(Object.keys(secrets).length ? { secrets } : {}),
      };
      return item ? connectionsApi.update(item.id, payload) : connectionsApi.create(payload);
    },
    onSuccess: () => {
      onSaved();
      onClose();
    },
  });

  return (
    <Modal
      title={item ? `Conexão — ${item.name}` : 'Nova conexão (WhatsApp Cloud API)'}
      onClose={onClose}
    >
      <div className="space-y-4">
        <Field label="Nome *">
          <input
            className={inputClass}
            value={name}
            maxLength={160}
            onChange={(event) => setName(event.target.value)}
          />
        </Field>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Número exibido">
            <input
              className={inputClass}
              value={phoneNumber}
              placeholder="+55 85 90000-0000"
              onChange={(event) => setPhoneNumber(event.target.value)}
            />
          </Field>
          <Field label="ID do número (phone_number_id)">
            <input
              className={inputClass}
              value={externalId}
              onChange={(event) => setExternalId(event.target.value)}
            />
          </Field>
        </div>
        <Field label="Departamento das conversas novas">
          <select
            className={inputClass}
            value={departmentId}
            onChange={(event) => setDepartmentId(event.target.value)}
          >
            <option value="">O principal do workspace</option>
            {departments.map((department) => (
              <option key={department.id} value={department.id}>
                {department.name}
              </option>
            ))}
          </select>
        </Field>
        <Field
          label="Token de acesso"
          hint={
            item?.secretsSet.accessToken
              ? 'Já configurado — preencha só para trocar.'
              : 'Token permanente do usuário do sistema na Meta.'
          }
        >
          <input
            type="password"
            autoComplete="off"
            className={inputClass}
            value={accessToken}
            onChange={(event) => setAccessToken(event.target.value)}
          />
        </Field>
        <Field
          label="App secret"
          hint={
            item?.secretsSet.appSecret
              ? 'Já configurado — preencha só para trocar.'
              : 'Confere a assinatura das mensagens recebidas.'
          }
        >
          <input
            type="password"
            autoComplete="off"
            className={inputClass}
            value={appSecret}
            onChange={(event) => setAppSecret(event.target.value)}
          />
        </Field>
        <Toggle
          checked={isActive}
          onChange={setIsActive}
          label="Ativa"
          hint="Desativada, o webhook ignora o que chegar."
        />
        {save.error && (
          <ErrorNote>{errorMessage(save.error, 'Não foi possível salvar')}</ErrorNote>
        )}
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>Cancelar</Button>
          <Button
            variant="primary"
            disabled={!name.trim() || save.isPending}
            onClick={() => save.mutate()}
          >
            {item ? 'Salvar' : 'Criar conexão'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
